import { Checkmark, type CarbonIconType } from '@carbon/icons-react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
  type Placement,
} from '@floating-ui/react';
import { Fragment, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { cx } from '@/shared/lib/cx';
import s from './Overlay.module.css';

export type MenuEntry =
  | {
      type?: 'item';
      id: string;
      label: string;
      icon?: CarbonIconType;
      onSelect?: () => void;
      /** Внутренний переход вместо действия. */
      to?: string;
      danger?: boolean;
      disabled?: boolean;
    }
  | {
      type: 'radio';
      id: string;
      label: string;
      checked: boolean;
      onSelect: () => void;
      icon?: CarbonIconType;
      disabled?: boolean;
    }
  | { type: 'divider'; id: string }
  | { type: 'group'; id: string; label: string };

export interface MenuProps {
  trigger: (props: Record<string, unknown> & { ref: (node: Element | null) => void; 'aria-expanded': boolean }) => ReactNode;
  items: ReadonlyArray<MenuEntry>;
  /** Информационный блок над пунктами (например, имя и роли в меню профиля). */
  header?: ReactNode;
  placement?: Placement;
  'aria-label'?: string;
}

type InteractiveEntry = Exclude<MenuEntry, { type: 'divider' } | { type: 'group' }>;

function isInteractive(entry: MenuEntry): entry is InteractiveEntry {
  return entry.type !== 'divider' && entry.type !== 'group';
}

/** Меню действий (WAI-ARIA menu button): стрелки, Home/End, поиск по первой букве, Esc. */
export function Menu({ trigger, items, header, placement = 'bottom-end', ...rest }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const interactive = items.filter(isInteractive);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);
  labelsRef.current = interactive.map((entry) => entry.label);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    disabledIndices: interactive.flatMap((entry, index) => (entry.disabled ? [index] : [])),
  });
  const typeahead = useTypeahead(context, { listRef: labelsRef, activeIndex, onMatch: open ? setActiveIndex : undefined });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role, listNavigation, typeahead]);

  const activate = (entry: InteractiveEntry) => {
    if (entry.disabled) return;
    entry.onSelect?.();
    setOpen(false);
  };

  let interactiveIndex = -1;

  return (
    <>
      {trigger({ ...getReferenceProps(), ref: refs.setReference, 'aria-expanded': open })}
      {open ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={0}>
            <div ref={refs.setFloating} className={s.menu} style={floatingStyles}>
              {header ? <div className={s.menuHeader}>{header}</div> : null}
              <div className={s.menuList} aria-label={rest['aria-label']} {...getFloatingProps()}>
                {items.map((entry) => {
                  if (entry.type === 'divider') return <div key={entry.id} role="separator" className={s.menuDivider} />;
                  if (entry.type === 'group')
                    return (
                      <div key={entry.id} role="presentation" className={s.menuGroupLabel}>
                        {entry.label}
                      </div>
                    );
                  interactiveIndex += 1;
                  const index = interactiveIndex;
                  const Icon = entry.icon;
                  const isRadio = entry.type === 'radio';
                  const itemProps = getItemProps({
                    ref: (node: HTMLElement | null) => {
                      listRef.current[index] = node;
                    },
                    role: isRadio ? 'menuitemradio' : 'menuitem',
                    'aria-checked': isRadio ? entry.checked : undefined,
                    'aria-disabled': entry.disabled || undefined,
                    tabIndex: activeIndex === index ? 0 : -1,
                    onClick: () => activate(entry),
                  });
                  const className = cx(s.menuItem, 'danger' in entry && entry.danger && s.menuItemDanger);
                  const body = (
                    <Fragment>
                      {Icon ? <Icon size={16} aria-hidden="true" /> : null}
                      <span className={s.menuItemLabel}>{entry.label}</span>
                      {isRadio && entry.checked ? <Checkmark size={16} className={s.menuCheck} aria-hidden="true" /> : null}
                    </Fragment>
                  );
                  if (entry.type !== 'radio' && entry.to && !entry.disabled) {
                    return (
                      <RouterLink key={entry.id} to={entry.to} className={className} data-active={activeIndex === index ? 'true' : undefined} {...(itemProps as Record<string, unknown>)}>
                        {body}
                      </RouterLink>
                    );
                  }
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={className}
                      data-active={activeIndex === index ? 'true' : undefined}
                      {...(itemProps as Record<string, unknown>)}
                    >
                      {body}
                    </button>
                  );
                })}
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
}
